import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import { useSeo } from "../hooks/useSeo";

const IMG = "https://images.unsplash.com/photo-1778731525503-04a3045e12f7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHw0fHxwcmVtaXVtJTIwbW9kZXJuJTIwYXBhcnRtZW50JTIwaW50ZXJpb3IlMjBsdXh1cnl8ZW58MHx8fHwxNzc5NzI2NDI3fDA&ixlib=rb-4.1.0&q=85";

export default function OverOns() {
  const { t, lang } = useI18n();
  useSeo({ title: t.seo.about.title, description: t.seo.about.description, path: "/over-ons", lang, image: IMG });

  return (
    <>
      <section className="container-wide pt-12 md:pt-20 pb-12 md:pb-16">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-12 flex items-center justify-between gap-6">
            <p className="eyebrow">{t.about.eyebrow}</p>
            <p className="hidden md:block text-xs uppercase tracking-[0.22em] text-muted-foreground font-bold">{t.editorial.aboutOfficeLabel}</p>
          </div>
          <h1 className="lg:col-span-12 text-[40px] leading-[0.98] tracking-[-0.025em] font-medium text-foreground sm:text-[60px] lg:text-[88px] xl:text-[104px]">
            {t.about.title}
          </h1>
          <div className="lg:col-span-7">
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">{t.about.lead}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/contact?intent=intake" className="btn-primary" data-testid="about-cta">
                {t.nav.bookIntake} <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
          <aside className="lg:col-span-5">
            <div className="border-t border-border pt-6 space-y-4 max-w-md ml-auto text-sm">
              <div className="flex items-baseline justify-between gap-6"><span className="font-display text-2xl text-foreground">3</span><span className="text-muted-foreground text-xs uppercase tracking-[0.18em] text-right max-w-[180px]">{t.editorial.aboutStat1Label}</span></div>
              <div className="flex items-baseline justify-between gap-6 border-t border-border pt-4"><span className="font-display text-2xl text-foreground">NL · EN · FR</span><span className="text-muted-foreground text-xs uppercase tracking-[0.18em] text-right max-w-[180px]">{t.editorial.aboutStat2Label}</span></div>
              <div className="flex items-baseline justify-between gap-6 border-t border-border pt-4"><span className="font-display text-2xl text-foreground">0%</span><span className="text-muted-foreground text-xs uppercase tracking-[0.18em] text-right max-w-[180px]">{t.editorial.aboutStat3Label}</span></div>
            </div>
          </aside>
        </div>
      </section>

      <section className="container-wide pb-16 md:pb-24">
        <div className="relative overflow-hidden border-y border-border">
          <img src={IMG} alt="Tip Top Property studio" className="w-full h-[42vh] md:h-[56vh] object-cover" />
          <span className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.25em] font-bold text-white/90 bg-black/30 backdrop-blur-sm px-3 py-1.5 rounded-sm">
            Fig. 04 — Studio interior
          </span>
        </div>
      </section>

      {/* Values — numbered editorial */}
      <section className="border-t border-border" data-testid="values-grid">
        <div className="container-wide py-20 md:py-28 grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4">
            <p className="eyebrow">{t.editorial.aboutValuesEyebrow}</p>
            <h2 className="mt-5 text-3xl md:text-4xl lg:text-5xl tracking-[-0.02em] font-medium text-foreground leading-[1.05]">
              {t.editorial.aboutValuesTitleBefore}<span className="italic text-primary">{t.editorial.aboutValuesHighlight}</span>
            </h2>
          </div>
          <ol className="lg:col-span-8 border-y border-border">
            {t.about.values.map((v, i) => (
              <li key={i} className="grid md:grid-cols-12 gap-6 py-8 md:py-10 border-b border-border last:border-b-0 items-baseline" data-testid={`value-${i}`}>
                <span className="md:col-span-1 font-display text-3xl text-accent leading-none">{String(i + 1).padStart(2, "0")}</span>
                <div className="md:col-span-4">
                  <h3 className="text-xl md:text-2xl text-foreground tracking-tight leading-snug">{v.t}</h3>
                </div>
                <p className="md:col-span-7 text-muted-foreground leading-relaxed max-w-xl">{v.b}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Manifesto block */}
      <section className="border-t border-border bg-secondary/40">
        <div className="container-wide py-20 md:py-28 grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-3"><p className="eyebrow">{t.editorial.aboutManifestoEyebrow}</p></div>
          <p className="lg:col-span-9 font-display text-2xl md:text-3xl lg:text-[40px] leading-[1.15] tracking-[-0.015em] text-foreground">
            {t.editorial.aboutManifestoBefore}<span className="italic text-primary">{t.editorial.aboutManifestoHighlight}</span>{t.editorial.aboutManifestoAfter}
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="container-wide py-16 md:py-20 grid md:grid-cols-12 gap-6 items-end">
        <p className="md:col-span-8 font-display text-2xl md:text-3xl text-foreground tracking-[-0.015em] leading-snug">
          {t.common.benelux}.<br />
          <span className="text-muted-foreground">{t.editorial.aboutFinalCta}</span>
        </p>
        <div className="md:col-span-4 flex md:justify-end">
          <Link to="/contact?intent=intake" className="btn-primary">{t.nav.bookIntake} <ArrowUpRight className="h-4 w-4" /></Link>
        </div>
      </section>
    </>
  );
}
